// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional permissions under AGPL-3.0 section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * The load-and-parse pipeline, shared by the worker and (if Workers are
 * unavailable) the main thread.
 *
 * The progressive Range ladder lives here. A GGUF's tensor table sits *after*
 * all of its metadata, and the metadata contains the tokenizer vocabulary — so
 * for real models the table starts 5–8 MB in, and for a few (gpt-oss) around
 * 13 MB. We ask for 8 MB, and only grow if the parser tells us it ran short.
 * Measured: one request suffices for the overwhelming majority of models.
 */

import { metaNumber, parseGguf, pickFromStringArray } from './gguf/parse';
import { LocalFileSource, READ_LADDER, RemoteSource, type ByteSource } from './gguf/source';
import { NeedMoreBytes, NotGguf, type ParsedGguf } from './gguf/types';

/** The token strings a chat template needs, resolved from the vocab by id. */
export interface SpecialTokens {
  bos?: string;
  eos?: string;
  pad?: string;
  unk?: string;
}

/**
 * Resolve BOS/EOS/PAD/UNK strings while the buffer is still in hand.
 *
 * These live in `tokenizer.ggml.tokens`, which the parser deliberately walks
 * without materialising. Doing this here means the template preview can show
 * real tokens without the parse ever building a 150 000-entry array.
 */
function resolveSpecialTokens(buf: Uint8Array, parsed: ParsedGguf): SpecialTokens {
  const tokens = parsed.kv.get('tokenizer.ggml.tokens');
  if (!tokens) return {};

  const ids = {
    bos: metaNumber(parsed.kv, 'tokenizer.ggml.bos_token_id'),
    eos: metaNumber(parsed.kv, 'tokenizer.ggml.eos_token_id'),
    pad: metaNumber(parsed.kv, 'tokenizer.ggml.padding_token_id'),
    unk: metaNumber(parsed.kv, 'tokenizer.ggml.unknown_token_id'),
  };
  const wanted = Object.values(ids).filter((v): v is number => typeof v === 'number');
  if (!wanted.length) return {};

  const picked = pickFromStringArray(buf, tokens, wanted);
  const out: SpecialTokens = {};
  for (const [key, id] of Object.entries(ids)) {
    if (typeof id === 'number' && picked.has(id)) out[key as keyof SpecialTokens] = picked.get(id);
  }
  return out;
}

export interface InspectProgress {
  phase: 'fetching' | 'parsing' | 'shards' | 'done';
  bytesRead: number;
  totalBytes: number | null;
  message: string;
}

export interface ShardInfo {
  index: number;
  of: number;
  tensorCount: number;
  bytes: number;
}

export interface InspectResult {
  parsed: ParsedGguf;
  /** Byte size of the whole model, summed across shards. */
  totalBytes: number | null;
  /** How many bytes we actually transferred or read. */
  bytesRead: number;
  elapsedMs: number;
  label: string;
  shards?: ShardInfo[];
  /** True when the source is a local file rather than the network. */
  local: boolean;
  specialTokens: SpecialTokens;
}

/**
 * Read from `source` until the header parses, growing the request when the
 * parser reports it ran off the end.
 */
export async function parseFromSource(
  source: ByteSource,
  onProgress?: (p: InspectProgress) => void,
  signal?: AbortSignal,
): Promise<{ parsed: ParsedGguf; bytesRead: number; specialTokens: SpecialTokens }> {
  let lastNeeded = 0;

  for (let step = 0; step < READ_LADDER.length; step++) {
    const want = Math.max(READ_LADDER[step], lastNeeded + 4096);
    onProgress?.({
      phase: 'fetching',
      bytesRead: 0,
      totalBytes: source.totalBytes,
      message:
        step === 0
          ? `Requesting the first ${(want / 1048576).toFixed(0)} MB…`
          : `Header is larger than expected — extending the read to ${(want / 1048576).toFixed(0)} MB…`,
    });

    const bytes = await source.read(want, signal);
    onProgress?.({
      phase: 'parsing',
      bytesRead: bytes.byteLength,
      totalBytes: source.totalBytes,
      message: `Read ${(bytes.byteLength / 1048576).toFixed(2)} MB — parsing the tensor table…`,
    });

    try {
      const parsed = parseGguf(bytes);
      return { parsed, bytesRead: bytes.byteLength, specialTokens: resolveSpecialTokens(bytes, parsed) };
    } catch (err) {
      if (err instanceof NeedMoreBytes) {
        // If the source has no more to give, the file itself is truncated.
        if (source.totalBytes !== null && bytes.byteLength >= source.totalBytes) {
          throw new NotGguf(
            'The file ends in the middle of its own header, so it is incomplete. If you downloaded it, the download was cut short.',
          );
        }
        lastNeeded = err.neededAtLeast;
        continue;
      }
      throw err;
    }
  }

  throw new NotGguf(
    `This model's header is larger than ${(READ_LADDER[READ_LADDER.length - 1] / 1048576).toFixed(0)} MB, which is well beyond anything seen in the wild. Refusing to keep downloading.`,
  );
}

export interface InspectTarget {
  kind: 'remote' | 'local';
  /** Remote: the first shard's URL, then any further shards in order. */
  urls?: string[];
  file?: File;
  label: string;
  /** Total size of all shards, when the repo listing already told us. */
  knownTotalBytes?: number;
}

export async function inspect(
  target: InspectTarget,
  onProgress?: (p: InspectProgress) => void,
  signal?: AbortSignal,
): Promise<InspectResult> {
  const started = Date.now();

  if (target.kind === 'local') {
    if (!target.file) throw new NotGguf('No file was provided.');
    const source = new LocalFileSource(target.file);
    const { parsed, bytesRead, specialTokens } = await parseFromSource(source, onProgress, signal);
    return {
      parsed,
      totalBytes: source.totalBytes,
      bytesRead,
      elapsedMs: Date.now() - started,
      label: target.label,
      local: true,
      specialTokens,
    };
  }

  const urls = target.urls ?? [];
  if (!urls.length) throw new NotGguf('No address to read.');

  const first = new RemoteSource(urls[0], target.label);
  const { parsed, bytesRead: firstBytes, specialTokens } = await parseFromSource(first, onProgress, signal);

  let bytesRead = firstBytes;
  let totalBytes = target.knownTotalBytes ?? first.totalBytes;
  const shards: ShardInfo[] = [];

  if (urls.length > 1) {
    shards.push({ index: 1, of: urls.length, tensorCount: parsed.tensors.length, bytes: first.totalBytes ?? 0 });
    let summed = first.totalBytes ?? 0;

    // Shards after the first carry only their own tensor table and almost no
    // metadata, so they are ~10 KB each. Reading them is what makes a split
    // 49 GB model sizeable at all.
    for (let i = 1; i < urls.length; i++) {
      onProgress?.({
        phase: 'shards',
        bytesRead,
        totalBytes,
        message: `Reading shard ${i + 1} of ${urls.length}…`,
      });
      const src = new RemoteSource(urls[i], `shard ${i + 1}`);
      const { parsed: shard, bytesRead: got } = await parseFromSource(src, undefined, signal);
      bytesRead += got;
      summed += src.totalBytes ?? 0;
      parsed.tensors.push(...shard.tensors);
      shards.push({ index: i + 1, of: urls.length, tensorCount: shard.tensors.length, bytes: src.totalBytes ?? 0 });
    }
    parsed.header.tensorCount = parsed.tensors.length;
    if (!target.knownTotalBytes) totalBytes = summed;
  }

  onProgress?.({ phase: 'done', bytesRead, totalBytes, message: 'Done.' });

  return {
    parsed,
    totalBytes,
    bytesRead,
    elapsedMs: Date.now() - started,
    label: target.label,
    shards: shards.length ? shards : undefined,
    local: false,
    specialTokens,
  };
}
